/**
 * Draw class logos onto a pdf-lib document.
 *
 * Shared by every report type's fill.js. Logos are stored with PDF-point
 * geometry (bottom-left origin), matching pdf-lib's drawImage coordinate space,
 * so no translation is needed here. Called after pdfForm.flatten() so logos sit
 * on top of the template content.
 *
 * @param {import('pdf-lib').PDFDocument} pdfDoc
 * @param {Array<{data:string,page:number,x:number,y:number,width:number,height:number}>} logos
 */
export async function drawLogos(pdfDoc, logos) {
  if (!Array.isArray(logos) || logos.length === 0) return;
  const pages = pdfDoc.getPages();

  for (const logo of logos) {
    try {
      const { data, page = 0, x, y, width, height } = logo;
      const pg = pages[page];
      if (!pg || !data) continue;

      const isPng = /^data:image\/png/i.test(data);
      const bytes = dataUriToBytes(data);
      const img = isPng ? await pdfDoc.embedPng(bytes) : await pdfDoc.embedJpg(bytes);

      // Fit inside the box preserving aspect ratio, centered - matches the
      // editor's CSS object-contain so what you place is what you get. (Plain
      // drawImage to {width,height} would stretch the logo to fill the box.)
      const s = Math.min(width / img.width, height / img.height);
      const drawW = img.width * s;
      const drawH = img.height * s;
      pg.drawImage(img, {
        x: x + (width - drawW) / 2,
        y: y + (height - drawH) / 2,
        width: drawW,
        height: drawH,
      });
    } catch (err) {
      // A single bad logo should never break PDF generation.
      console.error("drawLogos: failed to embed a logo", err);
    }
  }
}

function dataUriToBytes(dataUri) {
  const base64 = dataUri.includes(",") ? dataUri.slice(dataUri.indexOf(",") + 1) : dataUri;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
